/**
 * What a receipt parser proposed for an invoice or receipt, as the Review queue
 * shows it — the landing site for the receipt parser AI (30 September 2026).
 *
 * Eden's rulings: show the confidence as a number, and flag anything under 80%.
 * The 80% lives HERE, once, so the server's `flagged` booleans and anything the
 * screen works out for itself cannot disagree.
 *
 * Browser-safe: types, one constant and arithmetic. The parser itself, the
 * validator for its output and the switch that turns it on are server-only and
 * live in `apps/api/src/receipt-parser/`.
 */
import type { InvoiceLineKind } from "./vocabulary.js";

/** A proposed value under this confidence is flagged for a closer look. */
export const CONFIDENCE_REVIEW_THRESHOLD = 0.8;

/**
 * True when a score exists and is under the review threshold. No score is not a
 * low score.
 *
 * `threshold` is the threshold of the parser version that produced the score
 * (`document_parser.accuracy_threshold`, editable per version in Settings), and
 * the server sends it on every proposal as `AiProposal.threshold`. The constant
 * above is only the fallback.
 */
export function isLowConfidence(
  confidence: number | null | undefined,
  threshold: number = CONFIDENCE_REVIEW_THRESHOLD,
): boolean {
  return typeof confidence === "number" && confidence < threshold;
}

/**
 * How the site reads invoices and receipts with the AI.
 *
 * - `auto`: every invoice or receipt upload is read (what `RECEIPT_PARSER=stub`
 *   or `azure` alone used to mean).
 * - `manual`: nothing is read until somebody presses "Read with AI".
 * - `off`: nothing is read, and "Read with AI" is refused.
 *
 * `RECEIPT_PARSER=off` on the server is a CEILING: the site cannot turn reading
 * on above it (Eden, 1 October 2026).
 */
export const receiptReadingModes = ["off", "manual", "auto"] as const;
export type ReceiptReadingMode = (typeof receiptReadingModes)[number];

/** A worker check that fired, by name, with the sentence the screen shows. */
export interface AiProposalCheck {
  /** `ces_vendor`, `payment_number_scrubbed`, `arithmetic`, `duplicate_invoice`, `handwritten`, ... */
  check: string;
  message: string;
}

/** A unit the receipt names: the paper side of the work-order comparison row. */
export interface AiProposalAsset {
  unitNumber: string | null;
  vin: string | null;
  confidence: number | null;
}

/** `0.723` → `"72%"`. Whole percent, rounded down so 79.6% never reads as 80%. */
export function formatConfidence(confidence: number): string {
  const percent = Math.floor(Math.max(0, Math.min(1, confidence)) * 100);
  return `${percent}%`;
}

/** One proposed header value and how sure the parser was of it. */
export interface AiProposalField<T> {
  value: T;
  /**
   * The parser's score for this value. The receipt schema scores the vendor on
   * its own; the invoice number, date and total carry the DOCUMENT's score,
   * because the schema has no per-field score for them.
   */
  confidence: number | null;
  flagged: boolean;
}

export interface AiProposalLine {
  /** Printed candidate only; the reviewer confirms its meaning before posting. */
  serviceDate?: string | null;
  serviceDateLabel?: string | null;
  /** `proposed_cost_line.line_number` on the parser's extraction. 1-based. */
  lineNumber: number;
  kind: InvoiceLineKind;
  description: string;
  quantity: string | null;
  /** Scale 4, like `InvoiceLine.unitCost`. */
  unitCost: string | null;
  unitOfMeasure?: string | null;
  /**
   * A POSITIVE magnitude, as the entry form takes it; the kind carries the
   * sign. Null when the parser could not read a figure.
   */
  amount: string | null;
  confidence: number | null;
  flagged: boolean;
}

/**
 * `pending` covers both "queued" and "being read" — the screen says "Reading
 * with AI" for either. `completed` is a result a person can use; `failed` has a
 * `failureReason` instead.
 */
export type AiProposalStatus = "pending" | "completed" | "failed";

export interface AiProposal {
  /** The parser's own `extraction` row. Not a review item id. */
  extractionId: string;
  parserName: string;
  parserVersion: string;
  /** True for `receipt-extractor-stub`, whose output is invented, not read. */
  isStub: boolean;
  /**
   * The flag line this proposal was judged against: the producing parser
   * version's `accuracy_threshold`. Every `flagged` below used it.
   */
  threshold: number;
  status: AiProposalStatus;
  /** Whether a person has saved an invoice from this proposal. */
  reviewed: boolean;
  failureReason: string | null;
  /** The document's overall score. Null until completed. */
  confidence: number | null;
  flagged: boolean;
  vendorName: AiProposalField<string | null>;
  /** A `vendor` row whose name matches the proposed one exactly (case-insensitive). */
  vendorId: string | null;
  /**
   * TRBUD-412: the shop that did the work when another company bills for it, as
   * the AI read it off the bill (`vendor.performedBy`, e.g. Goodyear's "DEL BY"
   * shop, with its city). Null when the bill names none. Optional only so a
   * proposal built before the field reads as naming none.
   */
  performedByName?: string | null;
  /**
   * A `vendor` row whose name matches `performedByName` exactly, by the same
   * rule as `vendorId`, and never the matched biller. Null with no match: the
   * form then leaves "Work done by" empty and shows the printed name as a hint.
   */
  performedById?: string | null;
  invoiceNumber: AiProposalField<string | null>;
  invoiceDate: AiProposalField<string | null>;
  /** Optional for reads produced before service-date review was introduced. */
  serviceDate?: AiProposalField<string | null>;
  serviceDateLabel?: string | null;
  currency: string | null;
  totalAmount: AiProposalField<string | null>;
  lines: AiProposalLine[];
  /** Header fields and lines under the threshold, counted together. */
  flaggedCount: number;
  /**
   * The worker's checks that fired (CES named as the vendor, a card number
   * removed, the lines not adding up, a likely duplicate, handwriting), then the
   * parser's own warnings about the file, e.g. the stub saying it is a stub.
   */
  warnings: string[];
  /** The worker's checks that fired, by name. The same sentences lead `warnings`. */
  checks: AiProposalCheck[];
  /** The units the receipt names (unit number and VIN), in the order printed. */
  assets: AiProposalAsset[];
  /**
   * A NEWER read of this document that failed or is still running, beside the
   * completed one shown. A failed v4 must not hide a good v3, so the newest
   * COMPLETED read is the proposal and this says the later attempt exists.
   * Null when there is none.
   */
  newerAttempt: {
    extractionId: string;
    parserName: string;
    parserVersion: string;
    status: "pending" | "failed";
    failureReason: string | null;
  } | null;
}

/** The part of a proposal the queue ROW shows. */
export interface AiProposalSummary {
  status: AiProposalStatus;
  parserName: string;
  isStub: boolean;
  confidence: number | null;
  flagged: boolean;
  flaggedCount: number;
  /** See `AiProposal.threshold`. */
  threshold: number;
}

export function summariseAiProposal(proposal: AiProposal): AiProposalSummary {
  return {
    status: proposal.status,
    parserName: proposal.parserName,
    isStub: proposal.isStub,
    confidence: proposal.confidence,
    flagged: proposal.flagged,
    flaggedCount: proposal.flaggedCount,
    threshold: proposal.threshold,
  };
}

/**
 * What saving an invoice from an AI proposal recorded about that proposal: one
 * `parser_feedback` row per AI line. `added` counts lines the person typed that
 * had no AI original, which are not feedback about the parser and get no row.
 */
export interface AiFeedbackSummary {
  extractionId: string;
  accepted: number;
  corrected: number;
  rejected: number;
  added: number;
}

/**
 * Where the effective reading mode came from.
 *
 * - `stored`: somebody chose it on the site.
 * - `environment`: nothing is stored, so the server's `RECEIPT_PARSER` decides
 *   (`stub` or `azure` reads automatically).
 * - `ceiling`: `RECEIPT_PARSER=off`, which the site cannot lift.
 * - `unreadable`: the settings could not be read, so nothing new is read.
 */
export type ReceiptReadingModeBasis = "stored" | "environment" | "ceiling" | "unreadable";

/**
 * The receipt reading switch as the Review queue shows it. ALWAYS present: it
 * used to be null while the parser was off, so the screen could not show the
 * switch at all.
 */
export interface ReceiptParserState {
  mode: ReceiptReadingMode;
  modeBasis: ReceiptReadingModeBasis;
  /** The stored mode's version, for `PUT /review/reading-mode`. Null when unreadable. */
  modeVersion: number | null;
  /** Whether this viewer may change the mode (`setReceiptReading`). */
  canSetMode: boolean;
  /** The parser a new read would use, or null when none resolves. */
  parserName: string | null;
  parserVersion: string | null;
  isStub: boolean;
  /** That parser version's flag line. */
  threshold: number;
  /**
   * Why a read cannot happen, in a sentence, or null: the chosen version is
   * switched off or missing, the stub is chosen on a server that is not
   * `RECEIPT_PARSER=stub`, Azure is not configured here, or the settings could
   * not be read. Uploads still work; nothing is read.
   */
  problem: string | null;
}

/** One parser version on Settings → Receipt reading. */
export interface ReceiptParserVersionRow {
  parserId: string;
  name: string;
  version: string;
  isStub: boolean;
  active: boolean;
  threshold: number;
  /** Whether the stored choice names this row. */
  chosen: boolean;
  /** Whether it may be chosen on this server (the stub only with `RECEIPT_PARSER=stub`). */
  selectable: boolean;
  stats: {
    readsCompleted: number;
    readsFailed: number;
    linesAccepted: number;
    linesCorrected: number;
    linesRejected: number;
    lastReadAt: string | null;
  };
}

/** `GET /settings/receipt-reading`: effective, stored and environment side by side. */
export interface ReceiptReadingSettings {
  effective: ReceiptParserState;
  /** Null when the settings row could not be read. */
  stored: {
    /** Null: following the environment. */
    readingMode: ReceiptReadingMode | null;
    readingModeVersion: number;
    /** Null: following the environment. */
    parserId: string | null;
    parserChoiceVersion: number;
    updatedAt: string | null;
    updatedByLabel: string | null;
  } | null;
  environment: {
    /** `RECEIPT_PARSER`. */
    receiptParser: "off" | "stub" | "azure";
    /** `AZURE_AI_RECEIPT_AGENT_VERSION`, the default agent version. */
    azureAgentVersion: string | null;
    /** Whether the endpoint and agent id are set, so an Azure version can be read here. */
    azureConfigured: boolean;
  };
  versions: ReceiptParserVersionRow[];
  /**
   * Only on the answer to a save that changed which version reads, or turned
   * reading from Off to Auto (TRBUD-383): the Unaudited invoices and receipts it
   * started re-reading with that version, in the background (Eden, 5 October
   * 2026). Null when the save started none.
   */
  rereading?: ReceiptRereadStarted | null;
}

export interface ReceiptRereadStarted {
  /** How many Unaudited invoices and receipts were queued, read one at a time. */
  count: number;
  parserName: string;
  parserVersion: string;
}

/**
 * `PUT /review/reading-mode`: the switch as it now stands, and the re-read it
 * started. Turning reading from Off to Auto re-reads the Unaudited invoices and
 * receipts with the version in use (TRBUD-383), as a Settings save that
 * changes the version already did. Null when the switch started none.
 */
export interface ReceiptReadingModeSaved extends ReceiptParserState {
  rereading: ReceiptRereadStarted | null;
}
